import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { AuthStateRenderer, AuthenticationManager, Header, ErrorBoundary } from './components';

import { environment } from './config/environment';
import { useAppDispatch, useAppSelector } from './store';
import { setUser, setAuthenticated, setAuthorized, setCurrentRoute } from './store/slices/global.slice';

import './App.css';
import './styles/mobile.css';
import { AppRoutes, MainRouter } from './routes';

/**
 * Main application component that routes between development and production modes.
 *
 * In development mode (when environment.devAuthOff is true), renders AppWithoutAuth with mock authentication.
 * In production mode, renders AppWithAuth with Azure AD authentication.
 *
 * @returns {JSX.Element} The appropriate app variant based on environment configuration
 */
function App() {
  // Development mode without authentication
  if (environment.devAuthOff) {
    return <ErrorBoundary><AppWithoutAuth /></ErrorBoundary>;
  }

  // Production mode with Azure authentication
  return <ErrorBoundary><AppWithAuth /></ErrorBoundary>;
}

/**
 * Development mode application component with mock authentication.
 *
 * Provides a mock user for development without requiring Azure AD setup.
 * Automatically sets up Redux state with mock user data and initializes routing.
 *
 * @returns {JSX.Element} App layout with header and routes
 */
function AppWithoutAuth() {
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Mock user for development
  const mockUser = {
    id: 1,
    email: 'dev@localhost',
    display_name: 'Dev User',
    role: 'Administrator' as const
  };

  useEffect(() => {
    dispatch(setUser(mockUser));
    dispatch(setAuthenticated(true));
    dispatch(setAuthorized(true));
    dispatch(setCurrentRoute(location.pathname));
  }, [location.pathname, dispatch]);

  return (
    <Box className="app-layout">
      <Header user={mockUser} />
      <Box component="main" className="app-main" sx={{ bgcolor: 'background.default' }}>
        <AppRoutes />
      </Box>
    </Box>
  );
}

/**
 * Production mode application component with Azure AD authentication.
 *
 * Orchestrates authentication flow using AuthenticationManager and renders conditional UI
 * based on authentication state using AuthStateRenderer. Handles auth errors and access denial.
 *
 * @returns {JSX.Element} Authentication manager and state renderer with routes
 */
function AppWithAuth() {
  const { loading, user, isAuthenticated, isAuthorized } = useAppSelector(state => state.global);
  const [authError, setAuthError] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleAuthError = (message: string, error: any) => {
    setAuthError(true);
    setErrorMessage(`${message}: ${error.message || error.errorCode || 'Unknown error'}`);
  };

  const handleAccessDenied = () => {
    setAccessDenied(true);
  };

  return (
    <>
      <AuthenticationManager onAuthError={handleAuthError} onAccessDenied={handleAccessDenied} />
      <AuthStateRenderer
        loading={loading}
        authError={authError}
        accessDenied={accessDenied}
        isAuthenticated={isAuthenticated}
        isAuthorized={isAuthorized}
        user={user}
        errorMessage={errorMessage}
      >
        {ProtectedRoute => <MainRouter ProtectedRoute={ProtectedRoute} />}
      </AuthStateRenderer>
    </>
  );
}

export default App;
